/**
 * Jertscript2 Sarcastic Error Messages
 *
 * Error messages that mix French flair with CTO roasts.
 * References to blurry photos, ski passes, and pre-workout abound.
 */

/**
 * Error messages with sarcastic French flair
 */
export const ERROR_MESSAGES = {
  // Lexer errors
  unexpectedToken: (token: string, line: number) =>
    `Ligne ${line}: Token inattendu "${token}". Peut-etre que ta photo de profil floue t'empeche de voir ton code?`,

  unterminatedString: (line: number) =>
    `Ligne ${line}: Chaine non terminee. Comme tes promesses de livrer la feature a temps.`,

  invalidNumber: (line: number) =>
    `Ligne ${line}: Nombre invalide. Autant d'erreurs que de forfaits de ski dans ton portefeuille.`,

  invalidCharacter: (char: string, line: number) =>
    `Ligne ${line}: Caractere invalide "${char}". Meme les photos floues du CTO sont plus lisibles.`,

  unterminatedComment: (line: number) =>
    `Ligne ${line}: Commentaire non termine. Comme la collection de photos de ski jamais triee.`,

  // Parser errors - missing punctuation
  missingColon: (line: number) =>
    `Ligne ${line}: Deux-points manquants. Bois moins de pre-workout, ca aide a se concentrer.`,

  missingSemicolon: (line: number) =>
    `Ligne ${line}: Point-virgule oublie. Comme t'oublies de review les PRs.`,

  missingClosingParen: (line: number) =>
    `Ligne ${line}: Parenthese fermante manquante. Ta logique est aussi bancale que tes jambes apres leg day.`,

  missingOpeningParen: (line: number) =>
    `Ligne ${line}: Parenthese ouvrante manquante. Meme le CTO sait ouvrir une parenthese.`,

  missingClosingBrace: (line: number) =>
    `Ligne ${line}: Accolade fermante manquante. Comme ton planning qui n'a jamais de fin.`,

  missingOpeningBrace: (line: number) =>
    `Ligne ${line}: Accolade ouvrante manquante. Structure ton code comme tu structures... ah non, mauvais exemple.`,

  missingClosingBracket: (line: number) =>
    `Ligne ${line}: Crochet fermant manquant. Ton tableau est aussi incomplet que ta collection de photos nettes.`,

  // Parser errors - expected elements
  expectedExpression: (line: number) =>
    `Ligne ${line}: Expression attendue. J'ai vu de meilleures compositions dans des photos de parking.`,

  expectedIdentifier: (keyword: string, line: number) =>
    `Ligne ${line}: Identifiant attendu apres "${keyword}". Nomme tes variables mieux que "epic_shot_final_v2_FINAL".`,

  expectedFunctionName: (line: number) =>
    `Ligne ${line}: Nom de fonction attendu. Les fonctions ont besoin de noms, contrairement aux 10000 photos non triees du CTO.`,

  expectedParameterName: (line: number) =>
    `Ligne ${line}: Nom de parametre attendu. Meme le CTO nomme ses forfaits de ski.`,

  expectedType: (line: number) =>
    `Ligne ${line}: Type attendu. En TypeScript, les types sont importants. Contrairement a la mise au point dans les photos d'action.`,

  expectedArrow: (line: number) =>
    `Ligne ${line}: Fleche => attendue. Cette fonction flechee est aussi perdue que le CTO pendant un meeting.`,

  // Type errors
  typeMismatch: (expected: string, actual: string, line: number) =>
    `Ligne ${line}: Type "${expected}" attendu mais "${actual}" recu. Comme attendre une photo nette et recevoir du flou.`,

  undefinedVariable: (name: string, line: number) =>
    `Ligne ${line}: Variable "${name}" non definie. Elle est aussi introuvable qu'une photo nette dans la galerie du CTO.`,

  undefinedFunction: (name: string, line: number) =>
    `Ligne ${line}: Fonction "${name}" non definie. Elle existe autant que le planning respecte.`,

  cannotAssignToConst: (name: string, line: number) =>
    `Ligne ${line}: Tu ne peux pas reassigner "${name}". Un croissant declare reste declare, comme l'amour du CTO pour le ski.`,

  // Function errors
  wrongArgumentCount: (expected: number, actual: number, line: number) =>
    `Ligne ${line}: ${expected} arguments attendus, ${actual} recus. Compte comme le CTO compte ses objectifs de camera.`,

  invalidReturnType: (expected: string, actual: string, line: number) =>
    `Ligne ${line}: Retour de type "${expected}" attendu, "${actual}" recu. Classique surpomesse, sous-livraison.`,

  missingReturn: (line: number) =>
    `Ligne ${line}: Retour manquant. Ta fonction va nulle part, comme les skills de composition du CTO.`,

  // Class/Interface errors
  duplicateProperty: (name: string, line: number) =>
    `Ligne ${line}: Propriete "${name}" deja definie. Comme les 50 copies de la meme photo floue.`,

  invalidAccessModifier: (modifier: string, line: number) =>
    `Ligne ${line}: Modificateur d'acces "${modifier}" invalide. Choisis entre prive, publique, ou protege.`,

  // Import/Export errors
  moduleNotFound: (name: string, line: number) =>
    `Ligne ${line}: Module "${name}" introuvable. Il est aussi perdu que le CTO pendant le daily standup.`,

  invalidImport: (line: number) =>
    `Ligne ${line}: Import invalide. Ton import est aussi casse que l'autofocus du CTO en basse lumiere.`,

  invalidExport: (line: number) =>
    `Ligne ${line}: Export invalide. Tu ne peux pas exporter ca, comme le CTO ne peut pas exporter des photos nettes.`,

  // Async errors
  awaitOutsideAsync: (line: number) =>
    `Ligne ${line}: "attendre" utilise en dehors d'une fonction asynchrone. Comme attendre que le CTO finisse de trier ses photos.`,

  // Generic errors
  syntaxError: (message: string, line: number) =>
    `Ligne ${line}: Erreur de syntaxe - ${message}. Ton code a l'energie d'une photo prise avec le capuchon d'objectif.`,

  internalError: (message: string) =>
    `Erreur interne: ${message}. Si tu vois ca, meme le compilateur est aussi perdu que le CTO pendant ski season.`,
};

/**
 * Formats an error message with the croissant prefix
 * @param message The error message
 * @returns Formatted error message
 */
export function formatError(message: string): string {
  return `Erreur Jertscript: ${message}`;
}

/**
 * Formats an error with line information and context
 * @param message The error message
 * @param line The line number
 * @param column Optional column number
 * @returns Formatted error with location
 */
export function formatErrorWithLocation(
  message: string,
  line: number,
  column?: number
): string {
  const location = column !== undefined ? `${line}:${column}` : `${line}`;
  return `[${location}] ${message}`;
}

/**
 * Creates a code snippet showing the error location
 * @param source The source code
 * @param line The line number (1-indexed)
 * @param column The column number (1-indexed)
 * @returns Formatted code snippet with error pointer
 */
export function createErrorSnippet(
  source: string,
  line: number,
  column: number
): string {
  const lines = source.split('\n');
  const errorLine = lines[line - 1] || '';
  const pointer = ' '.repeat(column - 1) + '^';

  return `  ${line} | ${errorLine}\n    | ${pointer}`;
}

/**
 * Gets a random sarcastic suffix for error messages
 */
export function getRandomSuffix(): string {
  const suffixes = [
    "C'est la vie, mais c'est aussi faux.",
    'Meme le CTO ferait mieux. En fait, non.',
    'Essaie avec moins de pre-workout.',
    'Peut-etre que leg day etait une mauvaise idee hier.',
    'Ta photo de profil floue approuve ce message.',
    'Autant d\'erreurs que de forfaits de ski non utilises.',
    'Ton code a la meme energie qu\'une photo de parking.',
    'Classique energie de powder day.',
  ];
  return suffixes[Math.floor(Math.random() * suffixes.length)];
}

/**
 * TypeScript-specific error messages for the generator phase
 */
export const GENERATOR_ERRORS = {
  unknownNodeType: (type: string) =>
    `Type de noeud inconnu: "${type}". Le generateur est aussi confus que le CTO devant un reflex.`,

  invalidTypeAnnotation: (type: string) =>
    `Annotation de type invalide: "${type}". Les types doivent etre valides, contrairement aux excuses pour les photos floues.`,

  unsupportedFeature: (feature: string) =>
    `Fonctionnalite non supportee: "${feature}". Comme le mode manuel sur l'appareil du CTO - jamais utilise.`,

  circularReference: () =>
    `Reference circulaire detectee. Ton code tourne en rond comme les histoires de ski du CTO.`,
};

/**
 * Warning messages (less severe than errors)
 */
export const WARNING_MESSAGES = {
  unusedVariable: (name: string, line: number) =>
    `Ligne ${line}: Variable "${name}" declaree mais jamais utilisee. Comme le trepied du CTO.`,

  unusedFunction: (name: string, line: number) =>
    `Ligne ${line}: Fonction "${name}" declaree mais jamais appelee. Comme les bonnes intentions de trier les photos.`,

  unreachableCode: (line: number) =>
    `Ligne ${line}: Code inaccessible detecte. Il est aussi atteignable que la nettete dans une photo de chairlift.`,

  implicitAny: (name: string, line: number) =>
    `Ligne ${line}: Variable "${name}" a un type implicite "quelconque". Aussi vague que "on part a 7h-ish pour les pistes".`,

  shadowedVariable: (name: string, line: number) =>
    `Ligne ${line}: Variable "${name}" masque une variable du scope parent. Comme les nuages masquent le soleil pendant la seule journee de ski.`,

  emptyBlock: (line: number) =>
    `Ligne ${line}: Bloc vide detecte. Il ne fait rien, comme la strategie de backup photo du CTO.`,

  deprecatedFeature: (feature: string, line: number) =>
    `Ligne ${line}: Fonctionnalite "${feature}" obsolete. Aussi depassee que l'ancien appareil du CTO... qu'il utilise toujours.`,
};
